import moment from 'moment/src/moment';
import merge from 'lodash-es/merge';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import URI from '@dw/urijs-esm';
import { uuid } from '../utils';
import { store } from '../store.js';
import find from 'lodash-es/find';
import filter from 'lodash-es/filter';
import * as router from '../redux/router';
import * as device from '../redux/device';
import * as auth from '../redux/auth';
import * as board from '../redux/board';
import * as signup from '../redux/signup';
import firestoreRedux from '@dreamworld/firestore-redux';
import * as GTM from './gtm.js';
import * as PostHog from './posthog.js';
import { isAnalyticsAllowed } from './utils.js';

// Generate tab as a uuid() for common event properties.
const tabID = uuid();
const deviceID = device.selectors.getId();

const defaultProperties = {
  'user_agent': window.navigator.userAgent,
  'tab': tabID,
  'site': 'PWA',
  'device_id': deviceID,
};

/**
 * By default add `user_agent` property into analytics event.
 * @param {String} name event name
 * @param {Object} properties event properties
 * @param {Object} options
 *  - @property {Boolean} trackReferrer track referrer details or not.
 */
export const logEvent = (name, properties = {}, { trackReferrer = false } = {}) => {
  const state = store.getState();
  let trackReferrerDetails = {};
  if (trackReferrer) {
    trackReferrerDetails = {
      'referrer_page': router.selectors.referrerPage(store.getState()),
      'referrer_page_trigger': router.selectors.referrerPageTrigger(store.getState())
    };
  }


  const accountId = router.selectors.accountId(state);
  const accountDetails = accountId ? {'account_id': accountId}: {};
  const eventProperties = merge({}, defaultProperties, trackReferrerDetails, accountDetails, properties);

  // PostHog first, and outside the consent check: it captures cookielessly until the visitor
  // opts in, so it keeps the events from visitors who never answer the banner — which is most of
  // the traffic arriving from ads. GTM stays behind consent; it sets cookies and has no
  // cookieless mode.
  PostHog.logEvent(name, eventProperties);

  if(!isAnalyticsAllowed()) {
    return;
  }

  GTM.logEvent(name, eventProperties);
}

/**
 * By default add `board_id`, `role`, and `effective_role` property into analytics event.
 * @param {String} name event name
 * @param {Object} properties event properties
 * @param {Boolean} privateBoard board is private or not.
 * @param {Object} options
 *  - @property {Boolean} trackReferrer track referrer details or not.
 */
export const logBoardEvent = async(name, properties = {}, privateBoard = false, { trackReferrer = false } = {}) => {
  let state = store.getState();
  const boardId = router.selectors.dialogBoardId(state) || router.selectors.pageBoardId(state);
  const accountId = router.selectors.accountId(state);
  const userId = auth.selectors.currentUserId(state);
  let defaultBoardProperties  = {
    'board_id': boardId,
    'account_id': accountId,
    'board_role': 'NONE',
    'board_effective_role': 'NONE'
  };

  if (!privateBoard && boardId) {
    let attrs = firestoreRedux.selectors.doc(state, 'boards', boardId);
    // Loads current board attrs if not loaded.
    if (isEmpty(attrs)) {
      try {
        const query = firestoreRedux.getDocById('boards', boardId, { once: true });
        attrs = await query.result;
      } catch(error) {}
    }

    const createdAt = attrs?.createdAt || 0;

    state = store.getState();
    const allBoardTeamMembers = firestoreRedux.selectors.collection(state, 'board-team-members');
    let currentBoardTeamMembers = filter(allBoardTeamMembers, { boardId });
    // Load current board's team members if not loaded.
    if (isEmpty(currentBoardTeamMembers)) {
      try {
        const query = firestoreRedux.query('board-team-members', { where: [['boardId', '==', boardId]],  once: true });
        currentBoardTeamMembers = await query.result;
      } catch(error) {}
    }

    state = store.getState();
    const role = get(find(currentBoardTeamMembers, { userId }), 'role', 'NONE');
    const permission = board.selectors.boardEffectivePermission(state, boardId);
    const effectiveRole = permission ? permission.hasAdmin() ? 'BOARD_ADMIN':  permission.hasWrite() ? 'TEAM_MEMBER': permission.hasRead() ? 'VISITOR': 'NONE': 'NONE';
    defaultBoardProperties = {...defaultBoardProperties, ...{
      'board_role': role,
      'board_effective_role': effectiveRole,
      'board_type': attrs && attrs.type || 'NONE',
      'is_template': attrs && attrs.template || false,
      'board_privacy': attrs && attrs.privacy || 'NONE',
      'board_owner': attrs && attrs.ownerId === userId || false,
      'board_active': board.selectors.isBoardActive(state, boardId) || false,
      'is_copied_board': board.selectors.isCopiedBoard(state, boardId) || false,
      'is_getting_started_board':  attrs && attrs.gettingStarted || false,
      'is_ai_generated': attrs?.importBoardDetail?.service?.toLowerCase() === 'ai' || false,
      'day_offset': createdAt ? moment().startOf('day').diff(moment(createdAt).startOf('day'), 'days') : 0
    }};
  }
  logEvent(name, merge({}, defaultProperties, defaultBoardProperties, properties), { trackReferrer });
}

/**
 * Track 'board created' event after fetching board attrs from Firestore.
 * Reads `creationMethod` and `cloneModificationType` from board attrs.
 * @param {String} boardId
 * @param {Object} additionalProperties extra properties to merge into the event.
 */
export const logBoardCreatedEvent = async (boardId, additionalProperties = {}) => {
  try {
    const query = firestoreRedux.getDocById('boards', boardId, { once: true, waitTillSucceed: true });
    const attrs = await query.result;
    const eventDetails = { 'creation_method': attrs?.creationMethod || 'CREATE', 'is_ai_generated': attrs?.importBoardDetail?.service?.toLowerCase() === 'ai' || false, ...additionalProperties };
    if (attrs?.cloneModificationType) {
      eventDetails['clone_modification_type'] = attrs.cloneModificationType;
    }
    logEvent('board created', eventDetails);
  } catch (error) {
    console.error('logBoardCreatedEvent failed:', error);
  }
};

/**
 * Track Template event.
 * By default add `template_id`, and `is_loggedin` property into defualt properties event.
 * @param {String} name event name
 * @param {Object} properties event properties
 * @param {Object} options
 *  - @property {Boolean} trackReferrer track referrer details or not.
 */
export const logTemplateEvent = async (name, properties = {}, { trackReferrer = false } = {}) => {
  let state = store.getState();
  const templateId = router.selectors.dialogBoardId(state) || router.selectors.pageBoardId(state);
  const userId = auth.selectors.currentUserId(state);

  let attrs = templateId ? firestoreRedux.selectors.doc(state, 'boards', templateId) : null;
  if (templateId && isEmpty(attrs)) {
    try {
      const query = firestoreRedux.getDocById('boards', templateId, { once: true });
      attrs = await query.result;
    } catch(error) {}
  }

  const createdAt = attrs?.createdAt || 0;

  const defaultTemplateProperties = {
    'template_id': templateId,
    'is_loggedin': !!userId,
    'day_offset': createdAt ? moment().startOf('day').diff(moment(createdAt).startOf('day'), 'days') : 0
  };

  logEvent(name, merge({}, defaultProperties, defaultTemplateProperties, properties), { trackReferrer });
}

/**
 * Track AI helper event.
 * Extends common event properties with optional `chat_id` from ai helper common.
 * @param {String} name event name
 * @param {Object} properties event properties including optional `chat_id`
 */
export const logAiHelperEvent = (name, properties = {}) => {
  logEvent(name, properties);
}

/**
 * Track video  event.
 * By default add `name`, `video_url` and 'page_url' property into defualt properties event.
 * @param {String} name event name
 * @param {Object} properties event properties
 * @param {Object} options
 *  - @property {Boolean} trackReferrer track referrer details or not.
 */
 export const logVideoEvent = (name, properties = {}, { trackReferrer = false } = {}) => {
  const state = store.getState();
  const videoUrl = router.selectors.videoUrl(state);
  const videoId = router.selectors.videoId(state);

  const uri = new URI();
  const pageUrl = uri.toString().replace(`${uri.protocol()}:\/\/${uri.host()}`, '');

  const defaultVideoProperties = {
    'name': videoId,
    'video_url': videoUrl,
    'page_url': pageUrl
  };

  logEvent(name, merge({}, defaultProperties, defaultVideoProperties, properties), { trackReferrer });
}


/**
 * Track signup event.
 * By default add `signup_source` property into defualt properties event.
 * @param {String} name event name
 * @param {Object} properties event properties
 * @param {Object} options
 *  - @property {Boolean} trackReferrer track referrer details or not.
 */
export const logSignupEvent = (name, properties = {}, { trackReferrer = false } = {}) => {
  const state = store.getState();
  const signupDetails = signup.selectors.signupDetails(state);
  const defaultSignupProperties = {
    'signup_source': signupDetails && signupDetails.source
  };
  logEvent(name, merge({}, defaultProperties, defaultSignupProperties, properties), { trackReferrer });
}

/**
 * Reset the PostHog session.
 * Should be called on logout to prevent the old user's session from leaking into the next user's events.
 */
export const reset = () => {
  if(!isAnalyticsAllowed()) {
    return;
  }

  PostHog.reset();
};

/**
 * Set user properties.
 * @param {String} userId
 * @param {Object} properties Regular user properties (overwrite).
 * @param {Boolean} resetSession Whether to reset the session.
 * @param {Object} setOnceProperties setOnce properties for GTM
 *   (and PostHog when `posthogSetOnceProperties` is omitted).
 * @param {Object} [posthogSetOnceProperties] Optional PostHog-specific setOnce
 *   payload. Use when PostHog requires its native naming (e.g. `$initial_utm_*`)
 *   and other platforms expect a different name.
 */
export const setUserProperties = (userId, properties, resetSession = false, setOnceProperties = {}, posthogSetOnceProperties) => {
  const phSetOnce = posthogSetOnceProperties === undefined ? setOnceProperties : posthogSetOnceProperties;

  // PostHog first, and outside the consent check, matching `logEvent`. This is the call that
  // identifies the user, and identifying is what joins their earlier cookieless events to their
  // account. Behind the gate it would never run for a visitor who logs in without answering the
  // banner — which is the case this whole path exists for.
  PostHog.setUserProperties(userId, properties, resetSession, phSetOnce);

  if(!isAnalyticsAllowed()) {
    return;
  }

  // GTM has no setOnce semantic — merge both sets and send as regular props.
  // Safe because callers use setOnceProperties only for values that are immutable on the source side.
  const merged = (!isEmpty(setOnceProperties) || !isEmpty(properties)) ? { ...setOnceProperties, ...properties } : properties;
  GTM.setUserProperties(userId, merged);
}

/**
 * Set directly identifying user properties, e.g. the email Google Ads matches on when a
 * conversion carries no gclid.
 *
 * PostHog only: GTM forbids personal data, and `setUserProperties` fans out to it.
 * Consent gated, unlike the pseudonymous properties `setUserProperties` sends to PostHog.
 * @param {String} userId
 * @param {Object} properties
 */
export const setIdentifyingUserProperties = (userId, properties) => {
  if(!isAnalyticsAllowed() || isEmpty(properties)) {
    return;
  }

  PostHog.setUserProperties(userId, properties);
}

/**
 * Set UTM user properties for both the latest and the first-touch (initial) campaign.
 * Latest fields are sent as regular `utm_*` user properties (overwriting) on all platforms.
 * Initial fields are sent with set-once semantics:
 *   - GTM → `initial_utm_*`
 *   - PostHog → `$initial_utm_*` (PostHog's native first-touch convention)
 * @param {String} userId The user ID to set
 * @param {Object} params
 *   @param {Object} [params.latest] Latest UTM, keyed without the `utm_` prefix
 *   @param {Object} [params.initial] Initial UTM, keyed without the `utm_` prefix
 */
export const setUserUtmProperties = (userId, { latest, initial } = {}) => {
  const latestProps = {};
  for (const key in (latest || {})) {
    latestProps[`utm_${key}`] = latest[key];
  }

  const initialPropsDefault = {};   // GTM
  const initialPropsPostHog = {};   // PostHog native ($-prefixed)
  for (const key in (initial || {})) {
    initialPropsDefault[`initial_utm_${key}`] = initial[key];
    initialPropsPostHog[`$initial_utm_${key}`] = initial[key];
  }

  setUserProperties(userId, latestProps, false, initialPropsDefault, initialPropsPostHog);
}
